import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    Map,
    Marker,
    NavigationControl,
    type MapRef,
} from '@vis.gl/react-maplibre';
import { yandexMapStyle } from '@/shared/lib/yandexMapStyle';
import { RequestError } from '@/shared/ui/RequestState';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { HouseCoordinates } from '../model/types';
import classes from './AddressPointMap.module.scss';

interface AddressPointMapProps {
    current: HouseCoordinates | null;
    proposed: HouseCoordinates | null;
    onSelect?: (point: HouseCoordinates) => void;
}

/** Показывает исходную точку и предпросмотр исправления координат */
export const AddressPointMap = ({ current, proposed, onSelect }: AddressPointMapProps) => {
    const map = useRef<MapRef>(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const center = proposed ?? current;

    const showProposed = proposed && (!current
        || proposed.latitude !== current.latitude || proposed.longitude !== current.longitude);

    /** Показывает обе точки после загрузки карты и изменения координат */
    const framePoints = useCallback(() => {
        if (!center) return;

        if (current && proposed) {
            map.current?.fitBounds([
                [Math.min(current.longitude, proposed.longitude), Math.min(current.latitude, proposed.latitude)],
                [Math.max(current.longitude, proposed.longitude), Math.max(current.latitude, proposed.latitude)],
            ], {
                padding: 60,
                maxZoom: 17,
                duration: 0
            });
        } else {
            map.current?.jumpTo({
                center: [center.longitude, center.latitude],
                zoom: 16
            });
        }
    }, [center, current, proposed]);

    useEffect(framePoints, [framePoints]);

    return <div>
        {!center &&
            <p>Точка ещё не определена. Вставьте координаты найденного дома.</p>
        }
        {failed &&
            <RequestError
                message="Не удалось загрузить карту. Проверьте адрес по ссылкам ниже."
                onRetry={() => {
                    setAttempt((value) => value + 1);
                    setFailed(false);
                }}
            />
        }
        <div
            className={failed
                ? classes.failedMap
                : classes.map
            }
            aria-label="Карта координат дома"
        >
            <Map
                key={attempt}
                ref={map}
                initialViewState={{
                    latitude: center?.latitude ?? 55.75,
                    longitude: center?.longitude ?? 37.62,
                    zoom: center ? 16 : 5
                }}
                mapStyle={yandexMapStyle}
                style={{
                    width: '100%',
                    height: '100%'
                }}
                onLoad={framePoints}
                onError={() => setFailed(true)}
                onClick={(event) => onSelect?.({
                    latitude: event.lngLat.lat,
                    longitude: event.lngLat.lng
                })}
            >
                <NavigationControl position="top-left" />
                {current &&
                    <Marker
                        latitude={current.latitude}
                        longitude={current.longitude}
                    >
                        <span
                            className={classes.current}
                            aria-label="Текущая точка"
                        >
                            Текущая
                        </span>
                    </Marker>
                }
                {showProposed &&
                    <Marker
                        latitude={proposed.latitude}
                        longitude={proposed.longitude}
                    >
                        <span
                            className={classes.proposed}
                            aria-label="Проверяемая точка"
                        >
                            Новая
                        </span>
                    </Marker>
                }
            </Map>
        </div>
        {onSelect && !failed &&
            <p>Нажмите на карту, чтобы выбрать точку. Изменения применятся после сохранения.</p>
        }
    </div>;
};
